import { useState } from "react"
import { useQuery, useMutation } from "urql"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2, Pencil, Zap } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { AlertRuleForm } from "./components/alert-rule-form"
import { AlertRulesQuery } from "@/lib/graphql/queries"
import { DeleteAlertRuleMutation, TestAlertRuleMutation } from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"
import { alertChannelLabels, type AlertChannelValue } from "@/lib/schemas"
import { Badge } from "@/components/ui/badge"

type AlertRuleItem = ResultOf<typeof AlertRulesQuery>["alertRules"]["nodes"][number]

const LIMIT = 25

const CONDITION_LABELS: Record<string, string> = {
  dlq_threshold: "DLQ Threshold",
  outbound_dlq_threshold: "Outbound DLQ Threshold",
}

const CHANNEL_BADGE_CLASSES: Record<string, string> = {
  WEBHOOK: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/25",
  EMAIL: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/25",
  SLACK: "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/25",
}

export function AlertRulesPage() {
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [search])

  const [result, reexecute] = useQuery({
    query: AlertRulesQuery,
    variables: { search, after: pagination.after, first: pagination.first },
  })

  const [, deleteRule] = useMutation(DeleteAlertRuleMutation)
  const [, testRule] = useMutation(TestAlertRuleMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [editRule, setEditRule] = useState<AlertRuleItem | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [testingId, setTestingId] = useState<string | null>(null)

  const columns: Column<AlertRuleItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Channel",
      cell: (row) => (
        <Badge
          variant="outline"
          className={`font-mono text-[0.6875rem] font-medium tracking-wide px-1.5 py-0 ${CHANNEL_BADGE_CLASSES[row.channel] ?? ""}`}
        >
          {alertChannelLabels[row.channel as AlertChannelValue] ?? row.channel}
        </Badge>
      ),
    },
    {
      header: "Condition",
      cell: (row) => (
        <span className="text-xs font-mono">
          {CONDITION_LABELS[row.conditionType] ?? row.conditionType}
        </span>
      ),
    },
    {
      header: "Threshold",
      cell: (row) => (
        <span className="text-xs font-mono tabular-nums">{row.threshold}</span>
      ),
    },
    {
      header: "Cooldown",
      cell: (row) => (
        <span className="text-xs font-mono">{row.cooldown}</span>
      ),
    },
    {
      header: "Status",
      cell: (row) =>
        row.enabled ? (
          <StatusBadge status="ACTIVE" />
        ) : (
          <StatusBadge status="INACTIVE" />
        ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={async (e) => {
              e.stopPropagation()
              setTestingId(row.id)
              const res = await testRule({ id: row.id })
              setTestingId(null)
              if (res.error) { toast.error(res.error.message); return }
              toast.success("Test alert sent successfully")
            }}
            disabled={testingId === row.id}
            aria-label="Test"
          >
            <Zap className={`h-3.5 w-3.5 ${testingId === row.id ? "animate-pulse" : ""}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Edit"
            onClick={(e) => {
              e.stopPropagation()
              setEditRule(row)
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            aria-label="Delete"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteId(row.id)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
      className: "w-32 text-right",
    },
  ]

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const res = await deleteRule({ id: deleteId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Alert rule deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Alert Rules" description="Manage alerting rules for DLQ threshold monitoring">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create Rule
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search alert rules..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.alertRules.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.alertRules.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No alert rules"
        emptyDescription="Create an alert rule to get notified when DLQ thresholds are exceeded"
        onRowClick={(row) => setEditRule(row)}
      />

      <AlertRuleForm
        open={formOpen || !!editRule}
        onOpenChange={(open) => {
          if (!open) {
            setFormOpen(false)
            setEditRule(null)
          }
        }}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
        initialData={editRule ? {
          id: editRule.id,
          name: editRule.name,
          conditionType: editRule.conditionType,
          threshold: editRule.threshold,
          webhookUrl: editRule.webhookUrl,
          channel: editRule.channel,
          emailConfig: editRule.emailConfig,
          slackConfig: editRule.slackConfig,
          cooldown: editRule.cooldown,
          enabled: editRule.enabled,
        } : undefined}
      />

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Alert Rule"
        description="This will permanently delete this alert rule."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />
    </div>
  )
}
